import React, { useRef, useState, useEffect, useCallback } from 'react';

interface SignatureCaptureProps {
  onComplete: (base64: string) => void;
  existingSignature: string | null;
}

const SignatureCapture: React.FC<SignatureCaptureProps> = ({
  onComplete,
  existingSignature,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    // If we have an existing signature, draw it
    if (existingSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        setHasDrawn(true);
      };
      img.src = existingSignature;
    }
  }, []);

  const getTouchPos = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    };
  }, []);

  const getMousePos = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  const startDraw = (pos: { x: number; y: number }) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const moveDraw = (pos: { x: number; y: number }) => {
    if (!isDrawing) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const endDraw = () => {
    setIsDrawing(false);
  };

  // Touch handlers
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    startDraw(getTouchPos(e));
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    moveDraw(getTouchPos(e));
  };

  // Mouse handlers (for QA testing in browser)
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    startDraw(getMousePos(e));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    moveDraw(getMousePos(e));
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    setHasDrawn(false);
  };

  const handleContinue = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasDrawn) return;
    const base64 = canvas.toDataURL('image/png');
    onComplete(base64);
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e5e5e5',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 700,
            color: '#1a1a2e',
          }}
        >
          Draw Your Signature
        </h1>
      </div>

      {/* Instruction */}
      <div
        style={{
          padding: '20px 24px 12px',
          textAlign: 'center',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: '15px',
            color: '#6b7280',
            lineHeight: '22px',
          }}
        >
          Use your finger to draw your signature below. This will be applied to
          all documents.
        </p>
      </div>

      {/* Canvas area */}
      <div
        style={{
          flex: 1,
          margin: '0 20px',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div
          style={{
            flex: 1,
            border: '2px dashed #d1d5db',
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#ffffff',
            maxHeight: '200px',
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              touchAction: 'none',
              cursor: 'crosshair',
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={endDraw}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
          />
        </div>
      </div>

      {/* Bottom actions */}
      <div style={{ padding: '16px 20px 24px' }}>
        <button
          onClick={handleClear}
          disabled={!hasDrawn}
          style={{
            display: 'block',
            margin: '0 auto 12px',
            padding: '10px 20px',
            background: 'none',
            border: 'none',
            fontSize: '15px',
            fontWeight: 500,
            color: hasDrawn ? '#6366f1' : '#d1d5db',
            cursor: hasDrawn ? 'pointer' : 'default',
          }}
        >
          Clear
        </button>
        <button
          onClick={handleContinue}
          disabled={!hasDrawn}
          style={{
            width: '100%',
            padding: '16px',
            borderRadius: '12px',
            border: 'none',
            fontSize: '16px',
            fontWeight: 700,
            color: hasDrawn ? '#ffffff' : '#9ca3af',
            backgroundColor: hasDrawn ? '#6366f1' : '#e5e7eb',
            cursor: hasDrawn ? 'pointer' : 'default',
            minHeight: '52px',
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default SignatureCapture;
